/**
 * The marks on the two ways to start a game.
 *
 * Drawn rather than dropped in as artwork, for the same reason the tab icons
 * are: a bitmap arrives in its own colours and its own black, and would be the
 * only thing on the screen that could not follow the palette. As paths they
 * take `--gold` from the card and stay sharp on a phone rendering at three
 * times the size.
 *
 * **Filled, not outlined.** The first pass drew these the way the tab icons are
 * drawn — thin strokes, no fill — and at forty pixels a sword built from an
 * outlined blade, a fuller and a hollow pommel is a knot of lines rather than a
 * sword. These are silhouettes: one shape, read at a glance, in the same solid
 * style as the marks they were taken from. The tab icons stay stroked, and that
 * is fine — they are 21px chrome at the bottom of the screen, and these are the
 * thing you are choosing between.
 */

const frame = {
  className: 'mode-mark',
  viewBox: '0 0 48 64',
  fill: 'currentColor',
  'aria-hidden': true,
};

/**
 * One sword as a single filled outline, pointing up, drawn around its own
 * origin so the composition can place it by rotation alone.
 *
 * Traced as one path rather than assembled from parts: a silhouette with a seam
 * in it catches the light at the seam, and every joint here — blade to guard,
 * guard to grip — is a place two shapes would have met.
 */
const SWORD =
  'M0 -29 L2.5 -21 L2.5 3 L9.5 3.4 L9.5 6.6 L2.1 6.2 L1.8 13.6 L-1.8 13.6 ' +
  'L-2.1 6.2 L-9.5 6.6 L-9.5 3.4 L-2.5 3 L-2.5 -21 Z';

function Sword() {
  return (
    <>
      <path d={SWORD} />
      <circle cx="0" cy="15.8" r="2.6" />
    </>
  );
}

/**
 * Standard Contest — three swords, one upright behind two crossed.
 *
 * The upright one is drawn last and carries the light, so the pair behind it
 * read as depth rather than as clutter.
 */
export function StandardMark() {
  return (
    <svg {...frame}>
      <g opacity={0.5}>
        <g transform="translate(24 33) rotate(-30)">
          <Sword />
        </g>
        <g transform="translate(24 33) rotate(30)">
          <Sword />
        </g>
      </g>
      <g className="mode-mark-lead" transform="translate(24 32) scale(1.1)">
        <Sword />
      </g>
    </svg>
  );
}

/**
 * Random Draft — a struck coin with a spade on it.
 *
 * The crown is a hole rather than a second shape on top, so the mark is one
 * silhouette whatever it is drawn over — `evenodd` on a single path, with the
 * crown as an inner subpath. Painting it in the panel colour instead would put
 * a coin-shaped patch of the wrong background on anything that is not a panel.
 *
 * The rim beneath it is what makes it a struck coin rather than a disc, and it
 * is the same trick as the swords: dimmed, behind, so the face reads first.
 */
export function DraftMark() {
  return (
    <svg {...frame}>
      {/* The edge, seen from slightly above. Shallow: a deep one reads as a
          token or a poker chip rather than as struck metal. */}
      <path d="M4 30 L4 35 A20 12 0 0 0 44 35 L44 30 A20 12 0 0 1 4 30 Z" opacity={0.4} />
      {/*
        The face, as one path with four subpaths and `evenodd` doing the work.
        Outward in: the face fills, the border ring's two edges cut a groove
        through it, and the crown cuts through again. Every recess is a hole
        rather than a shape painted in the background colour, so the mark stays
        one silhouette over anything it is drawn on.
      */}
      <g className="mode-mark-lead">
        <path
          fillRule="evenodd"
          d={
            'M4 30 A20 12 0 1 1 44 30 A20 12 0 1 1 4 30 Z ' +
            // The pressed border, which is most of what separates a struck
            // coin from a disc with a picture on it.
            'M6.1 30 A17.9 10.2 0 1 1 41.9 30 A17.9 10.2 0 1 1 6.1 30 Z ' +
            'M7.1 30 A16.9 9.4 0 1 1 40.9 30 A16.9 9.4 0 1 1 7.1 30 Z ' +
            // A spade rather than a crown. A crown on a coin is a currency,
            // and this mode is not about who is king of anything — it is
            // about being dealt a hand you did not choose. The suit says that
            // in one shape, and it is the right kind of serious for it.
            //
            // Symmetric about x = 24 to the control point: a spade drawn
            // freehand is a spade that leans, and on a struck face a lean
            // reads as a misprint.
            'M24 22 C22.5 25.5 18.5 27.5 16 30 C13.4 32.6 13.8 36.4 17 36.6 ' +
            'C19.4 36.8 21.4 35.6 22.4 34 L21.2 38.2 L26.8 38.2 L25.6 34 ' +
            'C26.6 35.6 28.6 36.8 31 36.6 C34.2 36.4 34.6 32.6 32 30 ' +
            'C29.5 27.5 25.5 25.5 24 22 Z'
          }
        />
      </g>
    </svg>
  );
}
